/**
 * User Name Utility
 * 
 * Manages the user's name in localStorage for guestbook and Q&A features.
 * Persists the name across sessions so users don't have to re-enter it.
 */

const USER_NAME_KEY = 'wedding_user_name';
const NAME_MODAL_SKIP_KEY = 'weddingSkipNameModal';

/**
 * Get user name from localStorage
 * @returns {string|null} User name if present, null otherwise
 */
export const getUserName = () => {
  try {
    return localStorage.getItem(USER_NAME_KEY);
  } catch (error) {
    console.error('Failed to get user name from localStorage:', error);
    return null;
  }
};

/**
 * Save user name to localStorage
 * @param {string} name - The user's name to save
 */
export const saveUserName = (name) => {
  try {
    if (name && name.trim()) {
      localStorage.setItem(USER_NAME_KEY, name.trim());
    } else {
      localStorage.removeItem(USER_NAME_KEY);
    }
  } catch (error) {
    console.error('Failed to save user name to localStorage:', error);
  }
};

/**
 * Save user name from modal interaction.
 * Trims the provided name, persists it, and clears the modal skip flag.
 * @param {string} rawName - The name as entered in the modal
 * @returns {string|null} The trimmed name if saved, null otherwise
 */
export const saveUserNameFromModal = (rawName) => {
  if (!rawName) {
    return null;
  }
  const trimmed = rawName.trim();
  if (!trimmed) {
    return null;
  }
  try {
    localStorage.setItem(USER_NAME_KEY, trimmed);
    localStorage.removeItem(NAME_MODAL_SKIP_KEY);
  } catch (error) {
    console.error('Failed to save user name from modal:', error);
  }
  return trimmed;
};

/**
 * Mark that the user chose not to provide a name in the modal.
 * This prevents the modal from auto-opening again.
 */
export const markNameModalSkipped = () => {
  try {
    localStorage.setItem(NAME_MODAL_SKIP_KEY, 'true');
  } catch (error) {
    console.error('Failed to mark name modal as skipped:', error);
  }
};

/**
 * Check whether the name modal has been previously skipped.
 * @returns {boolean} True if skipped, false otherwise
 */
export const hasSkippedNameModal = () => {
  try {
    return localStorage.getItem(NAME_MODAL_SKIP_KEY) === 'true';
  } catch (error) {
    console.error('Failed to read name modal skip flag:', error);
    return false;
  }
};

/**
 * Clear user name from localStorage (for testing/debugging)
 */
export const clearUserName = () => {
  try {
    localStorage.removeItem(USER_NAME_KEY);
  } catch (error) {
    console.error('Failed to clear user name from localStorage:', error);
  }
};
